import { createSelector, createSlice } from '@reduxjs/toolkit';
import { cloneDeep, omit } from 'lodash';
import { Constants } from '@pwc/pwc-deals-web-common';

export const initialState = {
  content: null,
  status: {
    isDetailSuccess: false,
    isSharedDetailSuccess: false,
    isLoading: false,
    hasError: false,
    errorMessage: null,
    // enums: undefined | previousVersion
    errorType: undefined,
  },
  sharedStatus: null,
  sections: [],
  versions: null,
  sharedVersion: null,
  draftVersion: null,
  libraryItemsNew: {
    items: [],
  },
  libraryItemsShared: {
    items: [],
  },
  libraryItemGroups: null,
  tags: null,
  selectedVersion: null,
  isVersionHistoryToolbarOpen: false,
  richTextResolution: undefined,
};

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    resetAnalysis: () => initialState,
    fetchAnalysisBegan: (state) => ({
      ...state,
      isLoading: true,
      hasError: false,
      errorStatusCode: null,
    }),
    fetchAnalysisSucceeded: (state, action) => {
      return {
        ...state,
        ...action.payload,
        isLoading: false,
        hasError: false,
        errorStatusCode: null,
      };
    },
    fetchAnalysisFailed: (state, action) => ({
      ...state,
      isLoading: false,
      hasError: true,
      errorStatusCode: action.payload,
    }),
    fetchAnalysisDetailBegin: (state) => {
      state.status.isLoading = true;
      state.status.hasError = false;
      state.status.errorMessage = null;
    },
    fetchAnalysisDetailEnd: (state) => {
      state.status.isDetailSuccess = true;
      state.status.isSharedDetailSuccess = false;
    },
    fetchAnalysisContentStart: (state) => {
      state.status.isLoading = true;
      state.status.hasError = false;
    },
    fetchAnalysisContentEnd: (state) => {
      state.status.isLoading = false;
      state.status.isSharedDetailSuccess = true;
      state.status.isDetailSuccess = false;
    },
    fetchAnalysisContent: (state, action) => {
      const {
        content,
        content: { sections },
        richTextResolution,
      } = action.payload;

      const newContent = omit(content, ['sections']);
      state.content = newContent;
      state.richTextResolution = richTextResolution;
      state.sections = sections.map((section) => {
        const updatedItems = section.items.map((item) => {
          return { ...item, isLoading: true };
        });
        return { ...section, items: updatedItems };
      });
      state.status.isLoading = false;
      state.status.hasError = false;
    },
    fetchAnalysisContentError: (
      state,
      { payload: { errorType = undefined } = {} } = {}
    ) => {
      Object.assign(state, {
        content: initialState.content,
        sections: [],
        status: {
          ...state.status,
          errorType,
          isLoading: false,
          hasError: true,
          isSharedDetailSuccess: false,
        },
      });
    },
    fetchAnalysisContentErrorStopLoading: (state) => {
      state.status.isLoading = false;
    },
    fetchAnalysisLibraryItemGroups: (state, action) => {
      const { libraryItemGroups } = action.payload;
      state.libraryItemGroups = libraryItemGroups;
      state.status.isLoading = false;
      state.status.hasError = false;
    },
    fetchAnalysisDetailFailed: (state, action) => {
      const { isLoading, hasError, errorMessage } = action.payload;
      state.status.isLoading = isLoading;
      state.status.hasError = hasError;
      state.status.errorMessage = errorMessage;
      state.status.isDetailSuccess = false;
    },
    fetchLibraryItemsNew: (state, action) => {
      const { libraryItems } = action.payload;
      state.libraryItemsNew.items = libraryItems;
    },
    updateSharedStatus: (state, action) => {
      state.sharedStatus = { ...action.payload };
    },
    updateVersions: (state, action) => {
      state.versions = [...action.payload];
    },
    updateSectionItems: (state) => {
      const hasError = (libraryItem) => {
        const { type, error } = libraryItem;
        switch (type) {
          case 500:
            return error.isError;
          default:
            return error;
        }
      };
      const {
        libraryItemsNew: { items: libraryItems },
      } = state;
      const currentSections = cloneDeep(state.sections);
      if (currentSections && currentSections.length > 0) {
        const newSections = currentSections.map((section) => {
          const { items } = section;
          const newItems = items.map((item) => {
            const clonedItem = cloneDeep(item);
            const updatedLibItemIndex = libraryItems.findIndex(
              (_) => _.id === item.libraryItemId
            );
            if (updatedLibItemIndex > -1) {
              const libraryItem = cloneDeep(libraryItems[updatedLibItemIndex]);
              const updatedItem = {
                ...libraryItem,
                isLoading: false,
                hasError: hasError(libraryItem),
              };
              return {
                ...clonedItem,
                ...updatedItem,
              };
            }
            return {
              ...clonedItem,
            };
          });
          return {
            ...section,
            items: newItems,
            hasError: false,
            isLoading: true,
          };
        });
        state.sections = newSections;
      }
    },
    updateSectionItemsFailed: (state, action) => {
      const { allIds, allIdsDataById = {}, type } = action.payload;
      const currentSections = cloneDeep(state.sections);
      const updatedSections = currentSections.map((section) => {
        const newItems = section.items.map((item) => {
          const included = allIds.includes(item.libraryItemId);
          const itemData = allIdsDataById[item.libraryItemId] || {};

          return {
            ...item,
            type: (included ? type : item.type) || itemData.type,
            isLoading: included ? false : item.isLoading,
            hasError: included ? true : item.hasError,
            error: itemData.error,
          };
        });
        return { ...section, items: newItems };
      });
      state.sections = updatedSections;
    },

    updateSections: (state, action) => {
      const { newSections } = action.payload;
      state.sections = newSections;
    },
    fetchAnalysisVersionsSuccess: (state, action) => {
      const { versions } = action.payload;
      state.versions = versions;
    },
    fetchAllTags: (state, action) => {
      const { tags } = action.payload;
      state.tags = tags;
    },
    updateSelectionVersion: (state, action) => {
      state.selectedVersion = action.payload;
    },
    updateIsVersionHistoryToolbarOpen: (state, action) => {
      state.isVersionHistoryToolbarOpen = action.payload;
    },
  },
});
export default analysisSlice.reducer;

// Actions
export const {
  resetAnalysis,
  fetchAnalysisBegan,
  fetchAnalysisSucceeded,
  fetchAnalysisFailed,
  fetchAnalysisDetailBegin,
  fetchAnalysisDetailEnd,
  fetchAnalysisContentStart,
  fetchAnalysisContentEnd,
  fetchAnalysisContent,
  fetchAnalysisContentError,
  fetchAnalysisContentErrorStopLoading,
  fetchAnalysisLibraryItemGroups,
  fetchAnalysisDetailFailed,
  fetchLibraryItemsNew,
  updateSharedStatus,
  updateVersions,
  updateSectionItems,
  updateSectionItemsFailed,
  updateSections,
  updateSectionSharedItems,
  fetchAnalysisVersionsSuccess,
  fetchAllTags,
  setActiveVersion,
  updateSelectionVersion,
  updateIsVersionHistoryToolbarOpen,
} = analysisSlice.actions;

// Selectors
export const getCurrentAnalysis = (state) => state.analysis;
export const selectAnalysisContent = createSelector(
  getCurrentAnalysis,
  (slice) => slice.content
);

export const selectAnalysisRichTextResolution = createSelector(
  getCurrentAnalysis,
  (slice) => slice.richTextResolution
);

export const selectAnalysesisIfAvailable = (state) => {
  if (!state.analysis || !state.analysis?.content) {
    return null;
  }
  return {
    richTextResolution: state.analysis.richTextResolution,
    isShared: !!state.analysis.content.shareWithAll,
  };
};

export const selectAnalysisSections = createSelector(
  getCurrentAnalysis,
  (slice) => ({
    sections: slice.sections,
    isLoading: slice.status.isLoading,
    hasError: slice.status.hasError,
  })
);
export const getLibraryItemsNew = createSelector(
  getCurrentAnalysis,
  (slice) => slice.libraryItemsNew
);
export const getAnalysisStatus = createSelector(
  getCurrentAnalysis,
  (slice) => slice.status
);

export const selectAllTags = createSelector(
  getCurrentAnalysis,
  (slice) => slice.tags
);

export const getAnalysisIsShared = createSelector(
  getCurrentAnalysis,
  (slice) => slice.content && slice.content.shareWithAll
);

export const selectAnalysisVersions = createSelector(
  getCurrentAnalysis,
  (slice) => slice.versions
);

export const selectDraftVersion = createSelector(
  selectAnalysisVersions,
  (versions) =>
    versions &&
    versions.length > 0 &&
    versions.find(
      (item) =>
        item.versionStatus !== Constants.VERSION_STATUS.SHARED.value &&
        item.versionStatus !==
          Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
    )
);

export const getAnalysisLibraryItemGroups = createSelector(
  getCurrentAnalysis,
  (slice) => slice.libraryItemGroups
);

export const selectSharedVersion = createSelector(
  selectAnalysisVersions,
  (versions) =>
    versions &&
    versions.length > 0 &&
    versions.find(
      (item) =>
        item.versionStatus === Constants.VERSION_STATUS.SHARED.value ||
        item.versionStatus ===
          Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
    )
);

export const selectSharedStatus = createSelector(
  getCurrentAnalysis,
  (slice) => slice.sharedStatus
);

export const selectedVersionStatus = createSelector(
  getCurrentAnalysis,
  (slice) => slice.selectedVersion
);

export const selectedIsVersionHistoryToolbarOpen = createSelector(
  getCurrentAnalysis,
  ({ isVersionHistoryToolbarOpen }) => isVersionHistoryToolbarOpen
);
